import { alpha } from "@material-ui/core";
import {
  Box,
  Chip,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  MenuProps,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  HocBackdropLoader,
  HocBackdropLoaderProps,
} from "../HOC/HocBackdropLoader";
import { CoreService } from "../Services/Apis/CoreApis";
import { useAppDispatch, useAppSelector } from "../Store";
import { logoutAuth, useAuthStore } from "../Store/Slices/AuthSlice";
import { useCurrentBot } from "../Store/Slices/DashboardSlices";
import { formatString, isEmptyObject } from "../Utils";
import AppIcon from "./AppIcon";

import { logout } from "../Store/Slices/logout";
import AppProfileAvatar from "./ui/app-profile-avatar";
import { theme } from "../Customization/Theme";
import { ProtectedAppUiElement } from "./ProtectedComponents";

interface Props extends HocBackdropLoaderProps {
  anchorEl: HTMLElement | null;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  open: boolean;
  data?: any;
}

const AppUserDropdown: React.FC<Props> = ({
  anchorEl,
  setAnchorEl,
  open,
  data,
  setLoading,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentBot = useAppSelector(useCurrentBot);
  const { data: authData, userConfig } = useAppSelector(useAuthStore);
  const isWhatsappConfigured = userConfig?.payload?.whatsapp_enabled ?? false;
  const memoizedBottomMenuOptions = useMemo(() => {
    const permissionsData: any =
      userConfig?.payload?.allowed_permissions ?? null;

    if (isEmptyObject(permissionsData)) return;

    const result =
      userConfig?.payload?.menu_items?.left_bottom_menu_options ?? [];

    return result;
  }, [userConfig?.payload?.menu_items?.left_bottom_menu_options]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (curItem: any) => {
    setAnchorEl(null);
    if (curItem?.name && curItem?.name === "Logout") {
      let data = {
        user_id: authData?.data?.user?.id,
        workspace_id: authData?.data?.user?.workspace_id,
      };
      setLoading(true);
      CoreService.userLogout(data).finally(() => {
        // navigate("/login", { replace: true });
        const appVersion = window.localStorage.getItem("app_version");
        dispatch(logoutAuth(null));
        dispatch(logout()); // Dispatch the logout action
        window.localStorage.clear();
        if (appVersion) {
          window.localStorage.setItem("app_version", appVersion);
        }
        setTimeout(() => {
          window.location.replace("/login");
        }, 10); // Adjust the timeout duration as needed
        return;
      });
      return;
    }

    let URL = formatString(curItem?.url, [currentBot?.bot_id]);
    navigate(URL);
  };

  const accountType = userConfig?.payload?.sub_account_type;

  return (
    <StyledMenu
      id="demo-customized-menu"
      MenuListProps={{
        "aria-labelledby": "demo-customized-button",
      }}
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      style={{
        zIndex: 999,
      }}
    >
      <MyAccount>
        <Box
          sx={{ padding: "9px 16px", marginBottom: "8px" }}
          display="flex"
          flexDirection={"column"}
          justifyContent="flexStart"
          alignItems="center"
          className="myAccount"
          gap={"0.8rem"}
        >
          <AppProfileAvatar
            name={
              authData?.data?.user?.first_name +
              " " +
              authData?.data?.user?.last_name
            }
          />
          <p className="myAccountText">
            {authData?.data?.user?.first_name +
              " " +
              authData?.data?.user?.last_name}
          </p>
          {/* ,color:!accountType?theme.palette.default.white:theme.palette.primary.light */}
          <Chip
            style={{
              fontSize: "12px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.default.white,
            }}
            label={userConfig?.payload?.plan_code ?? "Free"}
            color="success"
            size="small"
          />
          <p
            className="myAccountText"
            style={{ color: theme.palette.default.darkGrey, fontSize: "13px" }}
          >
            WhatsApp number:{" "}
            {userConfig?.payload?.whatasapp_config?.whatsapp_no ??
              "not connected"}
          </p>
          <p
            className="myAccountText"
            style={{ color: theme.palette.default.darkGrey, fontSize: "13px" }}
          >
            WhatsApp business id:{" "}
            {userConfig?.payload?.whatasapp_config?.waba_id ?? "N/A"}
          </p>
        </Box>
      </MyAccount>
      <ProtectedAppUiElement moduleId="manage_profile_whatsapp">
        <MenuItem
          key={"manage_profile_whatsapp"}
          className="listItem"
          id="manage-profile_whatsapp"
          onClick={() =>
            handleClick({
              name: "Manage Profile",
              url: "/settings/manage-account/whatsapp-profile",
              avatar: "ri-user-line",
            })
          }
          disabled={!isWhatsappConfigured}
        >
          <ListItemIcon>
            <AppIcon
              size="20px"
              color={"#21233c"}
              title={"ri-user-line"}
              style={{
                height: "18px",
                width: "18px",
                margin: "0px 10px 0 4px",
                filter: "invert(100%)",
              }}
            />
          </ListItemIcon>
          <p style={{ fontSize: "14px" }}>{"Manage Profile"}</p>
        </MenuItem>
      </ProtectedAppUiElement>

      {memoizedBottomMenuOptions?.map((curElem: any, index: number) => {
        return (
          <MenuItem
            key={index}
            id={`manage-profile-${curElem?.label}`}
            className="listItem"
            onClick={() => handleClick(curElem)}
            disabled={
              curElem?.value !== "subscription"
                ? !isWhatsappConfigured ?? curElem?.isDisabled
                : false
            }
          >
            {/* id={`manage-profile-${curElem?.value}`} */}
            <ListItemIcon>
              {curElem?.icon ? (
                <img
                  loading="lazy"
                  style={{
                    height: "18px",
                    width: "18px",
                    margin: "0px 10px 0 4px",
                    filter: "invert(100%)",
                  }}
                  src={curElem?.icon}
                  alt={curElem?.label}
                />
              ) : null}
            </ListItemIcon>
            <p style={{ fontSize: "14px" }}>{curElem.label}</p>
          </MenuItem>
        );
      })}
      <Divider />
      {data &&
        data.length > 0 &&
        data.map((curElem: any, index: number) => {
          return (
            <MenuItem
              key={index}
              className="listItem"
              onClick={() => handleClick(curElem)}
              disableRipple
            >
              <ListItemIcon>
                <AppIcon size="20px" color={"#21233c"} title={curElem.avatar} />
              </ListItemIcon>
              <p style={{ fontSize: "14px" }}>{curElem.name}</p>
            </MenuItem>
          );
        })}
    </StyledMenu>
  );
};

export default HocBackdropLoader(AppUserDropdown);

export const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    sx={{ marginTop: "0.6rem" }}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    border: "1px solid #e5e9f2",
    boxShadow: "0 3px 12px 1px rgba(44,55,130,.15)",
    borderRadius: "8px",
    marginTop: theme.spacing(1),
    right: 6,
    minWidth: 300,
    maxWidth: 320,
    color: "rgb(55, 65, 81)",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiListItemText-primary": {
      fontFamily: theme.typography.fontFamily,
      fontSize: "1rem",
      fontWeight: 500,
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 16,
        color: theme.palette.secondary,
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.secondary.main
        ),
      },
    },
  },

  "& .MuiSwitch-root": {
    marginRight: "0.6rem",
  },
  "& .MuiFormControlLabel-root": {
    marginLeft: "-4px",
  },
}));

const MyAccount = styled(Box)`
  & {
    padding-bottom: 4px;
    .myAccount {
      padding-bottom: 0px;
      .myAccountText {
        color: ${alpha(theme.palette.default.text, 0.87)};
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        font-weight: 500;
      }
      .preferences {
        color: #21232c;
        font-size: 14px;
      }
    }
  }
`;
